import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// Import types from shared package (will be available after build)
// For now, define inline types
type DifficultyLevel = 'beginner' | 'intermediate' | 'advanced'
type PronunciationStrictness = 'relaxed' | 'moderate' | 'strict'

interface User {
  id: string
  username: string
  email?: string | null
  targetLanguage?: string | null
  languagesStudied?: string[]
  currentLevel: number
  totalXP: number
  studyStreak: number
  totalStudyTime: number
  speakingTime?: number
  reviewTime?: number
  difficultyLevel?: DifficultyLevel
  showFurigana?: boolean
  dailyReviewTarget?: number
  subscriptionTier: 'free' | 'standard' | 'premium'
  preferredVoiceId?: string | null
  pronunciationStrictness?: PronunciationStrictness
  createdAt?: string
  lastActivityDate?: string
  themePreference?: 'light' | 'dark'
  hasCompletedOnboarding?: boolean
}

interface UserState {
  user: User | null
  isLoading: boolean
  error: string | null
  themePreference: 'light' | 'dark'
  aiPersonality: string
  hasCompletedOnboarding: boolean

  // Actions
  setUser: (user: User) => void
  updateUser: (updates: Partial<User>) => void
  clearUser: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setThemePreference: (theme: 'light' | 'dark') => void
  setAiPersonality: (personality: string) => void
  setOnboardingComplete: () => void
}

const isOnboardingComplete = (user: User | null, aiPersonality?: string | null) => {
  if (!user) return false
  const hasLanguage = Boolean(user.targetLanguage)
  const hasDifficulty = Boolean(user.difficultyLevel)
  const hasPronunciation = Boolean(user.pronunciationStrictness)
  const hasDailyTarget = typeof user.dailyReviewTarget === 'number' && user.dailyReviewTarget > 0
  const hasPersonality = Boolean(aiPersonality)
  return hasLanguage && hasDifficulty && hasPronunciation && hasDailyTarget && hasPersonality
}

/**
 * User store - manages user profile and settings
 * Persisted to localStorage
 */
export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,
      error: null,
      hasCompletedOnboarding: false,
      themePreference:
        (typeof window !== 'undefined' && (localStorage.getItem('preferred-theme') as 'light' | 'dark' | null)) ||
        'light',
      aiPersonality:
        (typeof window !== 'undefined' && localStorage.getItem('ai-personality')) ||
        'friendly_tutor',

      setUser: (user) =>
        set((state) => {
          const theme = user.themePreference || state.themePreference
          const normalizedUser: User = {
            ...user,
            themePreference: theme,
          }
          const onboardingComplete =
            user.hasCompletedOnboarding ?? isOnboardingComplete(normalizedUser, state.aiPersonality)
          return {
            user: {
              ...normalizedUser,
              hasCompletedOnboarding: onboardingComplete,
            },
            error: null,
            themePreference: theme,
            hasCompletedOnboarding: onboardingComplete,
          }
        }),

      updateUser: (updates) =>
        set((state) => {
          const mergedUser = state.user ? ({ ...state.user, ...updates } as User) : null
          const onboardingComplete =
            updates.hasCompletedOnboarding ?? isOnboardingComplete(mergedUser, state.aiPersonality)
          return {
            user: mergedUser
              ? {
                  ...mergedUser,
                  hasCompletedOnboarding: onboardingComplete,
                }
              : null,
            themePreference:
              (updates.themePreference as 'light' | 'dark' | undefined) ?? state.themePreference,
            hasCompletedOnboarding: onboardingComplete,
          }
        }),

      clearUser: () => set({ user: null, error: null, hasCompletedOnboarding: false }),

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      setThemePreference: (theme) => {
        set((state) => ({
          themePreference: theme,
          user: state.user ? { ...state.user, themePreference: theme } : null,
        }))
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem('preferred-theme', theme)
          } catch {}
        }
      },

      setAiPersonality: (personality) => {
        set((state) => {
          const onboardingComplete = isOnboardingComplete(state.user, personality)
          return {
            aiPersonality: personality,
            hasCompletedOnboarding: onboardingComplete,
            user: state.user
              ? {
                  ...state.user,
                  hasCompletedOnboarding: onboardingComplete,
                }
              : null,
          }
        })
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem('ai-personality', personality)
          } catch {}
        }
      },

      setOnboardingComplete: () =>
        set((state) => ({
          hasCompletedOnboarding: true,
          user: state.user ? { ...state.user, hasCompletedOnboarding: true } : null,
        })),
    }),
    {
      name: 'user-storage',
      partialize: (state) => ({
        user: state.user,
        themePreference: state.themePreference,
        aiPersonality: state.aiPersonality,
        hasCompletedOnboarding: state.hasCompletedOnboarding,
      }), // Persist user + theme + personality preference
    }
  )
)
